import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Filter, RefreshCw, HeartPulse } from "lucide-react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import RequestsTable from "@/components/RequestsTable";
import RequestDetailDialog from "@/components/RequestDetailDialog";
import AddSickDayModal from "@/components/AddSickDayModal";
import HolidayCalendarView from "@/components/HolidayCalendarView";
import DayHolidaysDialog from "@/components/DayHolidaysDialog";

export default function SupervisorDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, cancelled: 0, total: 0 });
  const [open, setOpen] = useState(null);
  const [reference, setReference] = useState(null);
  const [deptFilter, setDeptFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [addSickOpen, setAddSickOpen] = useState(false);
  const [calendarRoleFilter, setCalendarRoleFilter] = useState("");
  const [dayContext, setDayContext] = useState(null);

  const load = async () => {
    const params = {};
    if (deptFilter) params.department = deptFilter;
    if (roleFilter) params.job_role = roleFilter;
    const [{ data: rs }, { data: st }] = await Promise.all([
      api.get("/holiday-requests", { params }),
      api.get("/stats"),
    ]);
    setRequests(rs); setStats(st);
  };

  useEffect(() => { api.get("/reference/all").then(({ data }) => setReference(data)); }, []);
  useEffect(() => { load(); /* eslint-disable-line */ }, [deptFilter, roleFilter]);

  // Deep-link from notification bell — open the request modal.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const rid = sp.get("request");
    if (!rid) return;
    api.get(`/holiday-requests/${rid}`).then(({ data }) => setOpen(data)).catch(() => {});
    sp.delete("request");
    navigate({ pathname: location.pathname, search: sp.toString() ? `?${sp.toString()}` : "" }, { replace: true });
  }, [location.search]); // eslint-disable-line

  const filtered = useMemo(() => {
    if (tab === "all") return requests;
    return requests.filter((r) => r.status === tab);
  }, [requests, tab]);

  return (
    <AppShell>
      <section className="max-w-[1400px] mx-auto px-3 sm:px-6 py-5 sm:py-8 space-y-5 sm:space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl font-semibold">Supervisor dashboard</h1>
            <p className="text-sm text-muted-foreground">{user.name} · {user.departments.join(", ") || "no departments assigned"}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="add-sick-day-btn"
              size="sm"
              onClick={() => setAddSickOpen(true)}
              className="gap-1.5 bg-rose-600 hover:bg-rose-500 text-white"
            >
              <HeartPulse className="w-3.5 h-3.5"/> Add a sick day
            </Button>
            <Button data-testid="sup-refresh-btn" size="sm" variant="outline" onClick={load} className="gap-1.5"><RefreshCw className="w-3.5 h-3.5"/> Refresh</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Pending" value={stats.pending} kind="pending"/>
          <Stat label="Approved" value={stats.approved} kind="approved"/>
          <Stat label="Rejected" value={stats.rejected} kind="rejected"/>
          <Stat label="Cancelled" value={stats.cancelled} kind="cancelled"/>
        </div>

        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
              <Filter className="w-4 h-4 text-muted-foreground"/>
              <Select value={deptFilter || "__all"} onValueChange={(v) => setDeptFilter(v === "__all" ? "" : v)}>
                <SelectTrigger data-testid="sup-dept-filter" className="max-w-[220px]"><SelectValue placeholder="All my departments"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All my departments</SelectItem>
                  {user.departments.map((d) => <SelectItem key={d} value={d} data-testid={`sup-dept-opt-${d}`}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={roleFilter || "__all"} onValueChange={(v) => setRoleFilter(v === "__all" ? "" : v)}>
                <SelectTrigger data-testid="sup-role-filter" className="max-w-[200px]"><SelectValue placeholder="All job roles"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All job roles</SelectItem>
                  {(reference?.job_roles || []).map((r) => <SelectItem key={r} value={r} data-testid={`sup-role-opt-${r}`}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList data-testid="sup-tabs" className="flex flex-nowrap overflow-x-auto max-w-full justify-start">
                <TabsTrigger value="pending" data-testid="tab-pending">Pending ({requests.filter((r) => r.status==="pending").length})</TabsTrigger>
                <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
                <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
                <TabsTrigger value="cancelled" data-testid="tab-cancelled">Cancelled</TabsTrigger>
                <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
                <TabsTrigger value="calendar" data-testid="sup-tab-calendar">Calendar</TabsTrigger>
              </TabsList>
              {tab === "calendar" ? (
                <TabsContent value="calendar" className="mt-4">
                  <HolidayCalendarView
                    requests={requests}
                    onOpen={setOpen}
                    jobRoles={reference?.job_roles || []}
                    activeRole={calendarRoleFilter}
                    onRoleClick={(r) => setCalendarRoleFilter(r === calendarRoleFilter ? "" : r)}
                    onDayClick={(date, list) => setDayContext({ date, requests: list })}
                  />
                </TabsContent>
              ) : (
                <TabsContent value={tab} className="mt-4">
                  <RequestsTable rows={filtered} onOpen={setOpen} emptyMessage={`No ${tab === "all" ? "" : tab + " "}requests.`}/>
                </TabsContent>
              )}
            </Tabs>
          </CardContent>
        </Card>
      </section>

      <RequestDetailDialog
        request={open}
        onClose={() => setOpen(null)}
        onUpdated={(updated) => {
          setRequests((rs) => updated.__deleted ? rs.filter((r) => r.id !== updated.id) : rs.map((r) => r.id === updated.id ? updated : r));
          load();
        }}
        supervisors={reference?.supervisors || []}
      />

      <AddSickDayModal
        open={addSickOpen}
        onOpenChange={setAddSickOpen}
        reference={reference}
        onCreated={() => load()}
      />

      <DayHolidaysDialog
        date={dayContext?.date}
        requests={dayContext?.requests || []}
        onClose={() => setDayContext(null)}
        onOpenRequest={(r) => { setDayContext(null); setOpen(r); }}
      />
    </AppShell>
  );
}

function Stat({ label, value, kind }) {
  return (
    <div className={`rounded-lg p-4 border ${kind ? `status-${kind}` : "bg-[#1E1E1E]"}`}>
      <div className="text-[11px] uppercase tracking-[0.08em] font-semibold opacity-80">{label}</div>
      <div className="font-heading text-3xl font-semibold mt-1 tabular-nums">{value}</div>
    </div>
  );
}
